/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import React, {useCallback, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {defaultsDeep, isObject, kebabCase} from 'lodash';
import {Swiper as BaseSwiper, SwiperSlide as BaseSwiperSlide} from 'swiper/react';

import {chakra} from '@chakra-ui/react';

import {Box, Button, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {swiperModules} from '@amblique/base/app/constants/options/swiper-modules';
import {SWIPER_MODULE_DEFAULTS, SWIPER_OPTIONS} from '@amblique/base/app/constants/swiper-options';
import {usePluginOptions} from '@amblique/base/app/hooks/use-plugin-options';
import {GalleryPauseIcon, GalleryPlayIcon} from '@amblique/king-living/app/components/icons';

const ChakraSwiper = chakra(BaseSwiper);
const ChakraSlide = chakra(BaseSwiperSlide);

ChakraSwiper.displayName = BaseSwiper.displayName;
ChakraSlide.displayName = BaseSwiperSlide.displayName;

/**
 * @param {{
 *  children: React.ReactNode,
 *  fallback: React.ReactNode,
 *  className: string,
 *  options: string | object,
 *  presets: string | string[],
 * }} args
 *
 * @returns {React.ReactElement}
 */
export const Swiper = ({
  children,
  fallback,
  className = '',
  variant = '',
  options,
  presets = '',
  slideProps = {},
  onSwiper: onSwiperProp,
  ...props
}) => {
  const swiperRef = useRef(null);
  const [autoplayPaused, setAutoplayPaused] = useState(false);

  presets = useMemo(() => (Array.isArray(presets) ? presets : presets?.split(/[\n\s,]+/) || []), [presets]);
  className = [...new Set(presets.map((preset) => `swiper-${kebabCase(preset)}`).concat(className.split(/[\n\s,]+/)))].join(' ');

  // Post-process callback: Handle module defaults
  // Note: resolvedOptions now includes responsive overrides already applied
  const postProcess = useCallback((resolvedOptions) => {
    for (const [moduleName, moduleDefaults] of Object.entries(SWIPER_MODULE_DEFAULTS)) {
      if (resolvedOptions[moduleName] === true) {
        resolvedOptions[moduleName] = {...moduleDefaults};
      } else if (isObject(resolvedOptions[moduleName])) {
        defaultsDeep(resolvedOptions[moduleName], moduleDefaults);
      }
    }

    // Auto correct the slidesPerGroup based on slidesPerView
    if (resolvedOptions.slidesPerGroup > resolvedOptions.slidesPerView) {
      resolvedOptions.slidesPerGroup = resolvedOptions.slidesPerView;
    }

    return resolvedOptions;
  }, []);

  // Use generic plugin options hook (now handles responsive options internally)
  const resolvedOptions = usePluginOptions('swiper', SWIPER_OPTIONS, presets, options, postProcess);
  const styles = useMultiStyleConfig('Swiper', {presets, options: resolvedOptions, variant});
  const {navigation, pagination, scrollbar} = resolvedOptions;

  const onSwiper = useCallback((swiper) => {
    swiperRef.current = swiper;
    onSwiperProp?.(swiper);

    setTimeout(() => {
      swiper.updateAutoHeight();
    }, 500);
  }, []);

  const toggleAutoplay = useCallback(() => {
    if (swiperRef.current) {
      const {running, paused} = swiperRef.current.autoplay;

      if (running && paused) {
        swiperRef.current.autoplay.resume();
        setAutoplayPaused(false);
      } else {
        swiperRef.current.autoplay.pause();
        setAutoplayPaused(true);
      }
    }
  }, [swiperRef.current, setAutoplayPaused]);

  return children?.length > 0 ? (
    <ChakraSwiper
      className={className}
      modules={swiperModules}
      onSwiper={onSwiper}
      {...resolvedOptions}
      {...styles.container}
      {...props}
    >
      {children.map((child, index) => (
        <ChakraSlide key={child.key || index}
          {...styles.slide}
          {...slideProps}
        >
          {child}
        </ChakraSlide>
      ))}

      {resolvedOptions.autoplay && (
        <Button className="swiper-autoplay" variant="link" onClick={toggleAutoplay} {...styles.autoplay}>
          {autoplayPaused ? <GalleryPlayIcon height="29px" width="29px" /> : <GalleryPauseIcon height="29px" width="29px" />}
        </Button>
      )}

      {(navigation || pagination) ? (
        <Box className="swiper-navigation-pagination" {...styles.paginationContainer}>
          {navigation && <Box className="swiper-button-prev" {...styles.navigation} />}
          {pagination && <Box className="swiper-pagination" {...styles.pagination} />}
          {navigation && <Box className="swiper-button-next" {...styles.navigation} />}
        </Box>
      ) : null}

      {scrollbar && <Box className="swiper-scrollbar" {...styles.scrollbar} />}
    </ChakraSwiper>
  ) : (typeof fallback === 'function' ? fallback() : fallback);
};

Swiper.displayName = 'Swiper';
Swiper.propTypes = {
  ...BaseSwiper.propTypes,
  children: PropTypes.node,
  fallback: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  className: PropTypes.string,
  variant: PropTypes.string,
  presets: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  options: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  slideProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
